import { IconButton, Stack, Td, Tr } from '@chakra-ui/react'
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { millisecondsToHumanReadableString } from '@/utils/time'

import { type UserContextType, useUser } from '@/client/context/user'
import { userHasSessionPageAccess } from '@/client/services/session'
import { getSeriesUrlById, getSessionUrlById, getTutorTrainingUrl } from '@/client/services/url'
import {
  requiresChangesBeforeReview,
  shouldFlagActivityDuration,
  VOLUNTEER_ACTIVITY_TO_LABEL,
} from '@/client/services/volunteerActivity'

import ApproveButton from '@/components/Admin/VolunteerHours/ApproveButton'
import RejectButton from '@/components/Admin/VolunteerHours/RejectButton'
import ShwLink from '@/components/ShwLink'

import ActivityModal from './ActivityModal/ActivityModal'
import { ApprovedIndicator } from './ApprovedIndicator'
import { CreateOrUpdateButton } from './CreateOrUpdateButton'
import { Links } from './Links'
import type { RelatedEntity, VolunteerActivityComponentAttributes } from './types'

const DESCRIPTION_MAX_LENGTH = 150

function getRelatedEntityLink(relatedEntity: RelatedEntity, user: UserContextType): string | null {
  if (relatedEntity == null) {
    return null
  }
  if (relatedEntity.type === 'host-session-prep' || relatedEntity.type === 'host-session') {
    const { hasAccess, restrictionType } = userHasSessionPageAccess(relatedEntity, user)
    if (hasAccess) {
      return getSessionUrlById(relatedEntity.id)
    }
    if (restrictionType === 'series') {
      return getSeriesUrlById(relatedEntity.seriesId ?? 0)
    }
  }
  if (
    relatedEntity.type === 'onboarding-buddy' ||
    relatedEntity.type === 'attend-new-tutor-onboarding-session' ||
    relatedEntity.type === 'attend-sat-bootcamp-onboarding-session'
  ) {
    return getSessionUrlById(relatedEntity.sessionId)
  }
  if (relatedEntity.type === 'tutor-training') {
    return getTutorTrainingUrl({
      slug: relatedEntity.slug,
      completed: relatedEntity.hasCurrentUserCompletedTraining,
    })
  }
  return null
}

export function TableRow({
  activity,
  asAdmin = false,
}: {
  activity: VolunteerActivityComponentAttributes
  asAdmin?: boolean
}): JSX.Element {
  const user = useUser()
  const [showAdminModal, setShowAdminModal] = useState(false)

  const {
    id,
    user_id,
    type,
    date,
    duration_seconds: durationSeconds,
    description,
    links,
    approved_at: approvedAt,
    created_at: createdAt,
    requested_changes_at: requestedChangesAt,
    last_submitted_at: lastSubmittedAt,
    related_entity: relatedEntity,
  } = activity

  const relatedEntityLink = getRelatedEntityLink(relatedEntity, user)

  const isApproved = approvedAt != null
  const typeText = VOLUNTEER_ACTIVITY_TO_LABEL[type]
  const dateTime = DateTime.fromJSDate(date)
  const dateTimeText = dateTime.toLocaleString(DateTime.DATE_SHORT)
  const durationMin = Math.ceil(durationSeconds / 60)
  const createdAtText = DateTime.fromJSDate(createdAt).toLocaleString(DateTime.DATE_SHORT)

  const requiresChanges = requiresChangesBeforeReview({ requestedChangesAt, lastSubmittedAt })
  const isResubmitted = requestedChangesAt != null && requestedChangesAt <= lastSubmittedAt

  const durationText = millisecondsToHumanReadableString(durationSeconds * 1000, {
    maxUnit: 'hour',
  })

  const canUserApproveHours = asAdmin && user_id !== user.id

  return (
    <>
      <ActivityModal
        open={showAdminModal}
        activity={activity}
        onClose={() => setShowAdminModal(false)}
      />
      <Tr>
        <Td>
          <ApprovedIndicator
            status={
              isApproved
                ? 'approved'
                : requiresChanges
                  ? 'requires-change'
                  : isResubmitted
                    ? 'resubmitted'
                    : 'pending'
            }
          />
        </Td>
        <Td>
          {relatedEntityLink != null ? (
            <ShwLink href={relatedEntityLink}>{typeText}</ShwLink>
          ) : (
            typeText
          )}
        </Td>
        <Td>
          {description.slice(0, DESCRIPTION_MAX_LENGTH)}
          {description.length >= DESCRIPTION_MAX_LENGTH ? '...' : ''}
        </Td>
        <Td>{dateTimeText}</Td>
        <Td
          bgColor={
            asAdmin && shouldFlagActivityDuration({ durationMin, activityType: type })
              ? 'error.subtle'
              : undefined
          }
        >
          {durationText}
        </Td>
        <Td maxWidth="200px" overflowX="clip">
          <Links links={links == null && relatedEntityLink != null ? [relatedEntityLink] : links} />
        </Td>
        <Td>{createdAtText}</Td>
        <Td>
          {canUserApproveHours && !isApproved ? (
            <Stack direction="row" spacing={4} align="center">
              <IconButton
                variant="icon"
                onClick={() => setShowAdminModal(true)}
                icon={<FontAwesomeIcon icon={faEllipsisH} />}
                aria-label="View Admin Modal"
              />
              <RejectButton id={id} requiresChangesBeforeReview={requiresChanges} />
              <ApproveButton id={id} requiresChangesBeforeReview={requiresChanges} />
            </Stack>
          ) : null}
          <CreateOrUpdateButton activity={activity} asAdmin={asAdmin} />
        </Td>
      </Tr>
    </>
  )
}
